import { jsPDF } from 'jspdf';

import autoTable from 'jspdf-autotable';

/* Builds and downloads the temple-styled PDF invoice for one order.
   Shared by the orders list and the order details page. */
export const downloadInvoice = (order) => {
  const doc = new jsPDF();

  /* ===== DIVINE PALETTE ===== */
  const MAROON = [74, 22, 8]; // deep temple maroon
  const SAFFRON = [234, 88, 12]; // sacred saffron
  const GOLD = [217, 160, 47]; // temple gold
  const GOLD_LIGHT = [253, 230, 138]; // soft gold text
  const CREAM = [255, 250, 240]; // page base
  const CARD = [253, 246, 233]; // card fill
  const INK = [74, 46, 5]; // body text
  const MUTED = [120, 92, 58]; // secondary text

  const setFill = (c) => doc.setFillColor(c[0], c[1], c[2]);
  const setDraw = (c) => doc.setDrawColor(c[0], c[1], c[2]);
  const setText = (c) => doc.setTextColor(c[0], c[1], c[2]);

  /* Horizontal gradient via thin vertical strips (jsPDF has no gradients) */
  const hGradient = (x, y, w, h, c1, c2, steps = 80) => {
    const sw = w / steps;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      setFill([
        Math.round(c1[0] + (c2[0] - c1[0]) * t),
        Math.round(c1[1] + (c2[1] - c1[1]) * t),
        Math.round(c1[2] + (c2[2] - c1[2]) * t)
      ]);
      doc.rect(x + i * sw, y, sw + 0.4, h, 'F');
    }
  };

  /* Small vector diamond ornament */
  const diamond = (cx, cy, s, c) => {
    setFill(c);
    doc.triangle(cx, cy - s, cx - s, cy, cx, cy, 'F');
    doc.triangle(cx, cy - s, cx + s, cy, cx, cy, 'F');
    doc.triangle(cx, cy + s, cx - s, cy, cx, cy, 'F');
    doc.triangle(cx, cy + s, cx + s, cy, cx, cy, 'F');
  };

  /* Gold line with a centred diamond — a divine section divider */
  const ornamentDivider = (y) => {
    setDraw(GOLD);
    doc.setLineWidth(0.4);
    doc.line(20, y, 96, y);
    doc.line(114, y, 190, y);
    diamond(105, y, 2.4, GOLD);
    diamond(99, y, 1.2, SAFFRON);
    diamond(111, y, 1.2, SAFFRON);
  };

  /* ===== PAGE BASE + GOLD FRAME ===== */
  setFill(CREAM);
  doc.rect(0, 0, 210, 297, 'F');

  setDraw(GOLD);
  doc.setLineWidth(1.1);
  doc.rect(8, 8, 194, 281);
  doc.setLineWidth(0.4);
  doc.rect(10.5, 10.5, 189, 276);

  /* ===== HEADER (gradient band) ===== */
  hGradient(10.5, 10.5, 189, 40, MAROON, SAFFRON);
  /* gold temple trim under the header */
  setFill(GOLD);
  doc.rect(10.5, 50.5, 189, 2.2, 'F');

  /* corner ornaments in the header */
  diamond(20, 20, 2, GOLD_LIGHT);
  diamond(190, 20, 2, GOLD_LIGHT);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(27);
  setText(GOLD_LIGHT);
  doc.text('PoojaMart', 20, 28);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  setText([255, 244, 224]);
  doc.text('Divine & Spiritual Collection', 20, 36);

  doc.setFontSize(7.5);
  setText(GOLD_LIGHT);
  doc.text('S A C R E D   •   B L E S S E D   •   A U T H E N T I C', 20, 44);

  /* Invoice label + meta, right aligned inside header */
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  setText(GOLD_LIGHT);
  doc.text('INVOICE', 190, 24, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setText([255, 244, 224]);
  doc.text(`No: #${order._id.slice(-8)}`, 190, 32, { align: 'right' });
  doc.text(`Date: ${new Date(order.createdAt).toLocaleDateString()}`, 190, 38, {
    align: 'right'
  });

  /* ===== STORE CONTACT ROW ===== */
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setText(MUTED);
  doc.text('PoojaMart E-Commerce Store', 20, 61);
  doc.text('support@shopease.com   |   +91 98765 43210', 20, 66);

  ornamentDivider(74);

  /* ===== BILL TO + INVOICE DETAILS CARD ===== */
  setFill(CARD);
  doc.roundedRect(14, 80, 182, 44, 4, 4, 'F');
  setDraw(GOLD);
  doc.setLineWidth(0.4);
  doc.roundedRect(14, 80, 182, 44, 4, 4, 'S');
  /* accent bar on the left of the card */
  setFill(SAFFRON);
  doc.roundedRect(14, 80, 2.4, 44, 2, 2, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  setText(SAFFRON);
  doc.text('BILL TO', 22, 90);
  doc.text('INVOICE DETAILS', 116, 90);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  setText(INK);
  doc.setFont('helvetica', 'bold');
  doc.text(`${order.customerName}`, 22, 99);
  doc.setFont('helvetica', 'normal');
  doc.text(`${order.phone}`, 22, 106);
  doc.text(`${order.address}`, 22, 113, { maxWidth: 86 });
  doc.text(`${order.city} - ${order.pincode}`, 22, 120);

  doc.text(`Invoice No : #${order._id.slice(-8)}`, 116, 99);
  doc.text(
    `Order Date : ${new Date(order.createdAt).toLocaleDateString()}`,
    116,
    106
  );
  doc.text(
    `Payment    : ${order.paymentId ? 'Paid Online' : 'Cash on Delivery'}`,
    116,
    113
  );
  doc.text(`Status     : ${order.status}`, 116, 120);

  /* ===== PRODUCTS TABLE ===== */
  autoTable(doc, {
    startY: 132,

    head: [['#', 'Product', 'Qty', 'Unit Price', 'Amount']],

    body: order.orderItems.map((item, i) => [
      i + 1,
      item.name,
      item.quantity,
      `Rs. ${item.price}`,
      `Rs. ${item.price * item.quantity}`
    ]),

    theme: 'grid',

    margin: { left: 14, right: 14 },

    headStyles: {
      fillColor: MAROON,
      textColor: GOLD_LIGHT,
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 10,
      cellPadding: 3.5
    },

    bodyStyles: {
      textColor: INK,
      fontSize: 9.5,
      cellPadding: 3.5
    },

    alternateRowStyles: {
      fillColor: CARD
    },

    columnStyles: {
      0: { halign: 'center', cellWidth: 12 },
      2: { halign: 'center', cellWidth: 18 },
      3: { halign: 'right', cellWidth: 32 },
      4: { halign: 'right', cellWidth: 34 }
    },

    styles: {
      lineColor: [235, 214, 178],
      lineWidth: 0.2
    }
  });

  const finalY = doc.lastAutoTable.finalY + 12;

  /* ===== TOTALS ===== */
  const subtotal = order.orderItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const discount = order.discountAmount || 0;

  /* Left: order summary + status badge */
  setFill(CARD);
  doc.roundedRect(14, finalY, 86, 40, 4, 4, 'F');
  setDraw(GOLD);
  doc.setLineWidth(0.4);
  doc.roundedRect(14, finalY, 86, 40, 4, 4, 'S');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  setText(SAFFRON);
  doc.text('ORDER SUMMARY', 20, finalY + 10);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  setText(INK);
  doc.text(`Total Items : ${order.orderItems.length}`, 20, finalY + 19);
  if (order.couponCode) {
    doc.text(`Coupon      : ${order.couponCode}`, 20, finalY + 26);
  }

  const statusColors = {
    Delivered: [34, 197, 94],
    Shipped: [59, 130, 246],
    Processing: [245, 158, 11],
    'Out For Delivery': [139, 92, 246]
  };
  const badge = statusColors[order.status] || [107, 114, 128];
  setFill(badge);
  doc.roundedRect(20, finalY + 30, 46, 8, 4, 4, 'F');
  setText([255, 255, 255]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text(order.status.toUpperCase(), 43, finalY + 35.4, {
    align: 'center'
  });

  /* Right: amount breakdown */
  let ry = finalY + 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  setText(MUTED);
  doc.text('Subtotal', 116, ry);
  setText(INK);
  doc.text(`Rs. ${subtotal}`, 196, ry, { align: 'right' });

  if (discount > 0) {
    ry += 7;
    setText(MUTED);
    doc.text('Discount', 116, ry);
    setText([22, 128, 60]);
    doc.text(`- Rs. ${discount}`, 196, ry, { align: 'right' });
  }

  /* Grand total band (gradient) */
  const bandY = ry + 6;
  hGradient(114, bandY, 82, 16, MAROON, SAFFRON);
  setDraw(GOLD);
  doc.setLineWidth(0.5);
  doc.rect(114, bandY, 82, 16);
  setText(GOLD_LIGHT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('TOTAL', 118, bandY + 10);
  doc.setFontSize(14);
  doc.text(`Rs. ${order.total}`, 192, bandY + 10.5, { align: 'right' });

  if (order.paymentId) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    setText(MUTED);
    doc.text(`Payment ID: ${order.paymentId}`, 196, bandY + 22, {
      align: 'right'
    });
  }

  /* ===== FOOTER (blessing) ===== */
  ornamentDivider(268);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  setText(SAFFRON);
  doc.text(
    'May this sacred purchase bring you peace, prosperity & divine blessings',
    105,
    276,
    { align: 'center' }
  );

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  setText(MUTED);
  doc.text('Thank you for shopping with PoojaMart', 105, 282, {
    align: 'center'
  });

  doc.save(`Invoice-${order._id}.pdf`);
};

export default downloadInvoice;
